import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createPlan, updatePlan, listDiscounts, discountLabel, Plan, Discount } from './api';
import { useToast } from '../../context/ToastContext';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

/** Modal para crear o editar un plan/membresía. */
export default function MembershipForm({ plan, onClose, onSaved }: { plan?: Plan | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const editing = !!plan;
  const [name, setName] = useState(plan?.name ?? '');
  const [code, setCode] = useState(plan?.code ?? '');
  const [classes, setClasses] = useState(plan?.classes_month != null ? String(plan.classes_month) : '');
  const [priceM, setPriceM] = useState(plan?.price_month != null ? String(plan.price_month) : '');
  const [priceT, setPriceT] = useState(plan?.price_trimester != null ? String(plan.price_trimester) : '');
  const [priceY, setPriceY] = useState(plan?.price_year != null ? String(plan.price_year) : '');
  const [defDisc, setDefDisc] = useState(plan?.default_discount_id != null ? String(plan.default_discount_id) : '');
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { listDiscounts().then((r) => setDiscounts(r.data)); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Ponle un nombre al plan'); return; }
    const payload = {
      name: name.trim(),
      code: code.trim() || null,
      classes_month: classes === '' ? null : Number(classes),
      price_month: priceM === '' ? null : Number(priceM),
      price_trimester: priceT === '' ? null : Number(priceT),
      price_year: priceY === '' ? null : Number(priceY),
      default_discount_id: defDisc === '' ? null : Number(defDisc),
    };
    setSaving(true);
    try {
      if (editing) { await updatePlan(plan!.membership_id, payload); toast.success('Plan actualizado'); }
      else { await createPlan(payload); toast.success('Plan creado'); }
      onSaved();
    } catch (err: any) { toast.error(err?.message || 'No se pudo guardar el plan'); }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{editing ? 'Editar plan' : 'Nuevo plan'}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Nombre del plan</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. 3 disciplinas" className={inputCls} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Clave (opcional)</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ej. 3D" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Clases / mes</label>
              <input type="number" inputMode="numeric" min="0" value={classes} onChange={(e) => setClasses(e.target.value)} placeholder="Ej. 12" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Precio mensual (MXN)</label>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={priceM} onChange={(e) => setPriceM(e.target.value)} placeholder="0.00" className={inputCls} />
            <p className="mt-1 text-xs text-content-3">Es el precio que usa la cobranza del mes.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Precio trimestre</label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={priceT} onChange={(e) => setPriceT(e.target.value)} placeholder="opcional" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Precio anual</label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={priceY} onChange={(e) => setPriceY(e.target.value)} placeholder="opcional" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Descuento por defecto</label>
            <select value={defDisc} onChange={(e) => setDefDisc(e.target.value)} className={inputCls}>
              <option value="">— Ninguno —</option>
              {discounts.map((d) => <option key={d.discount_id} value={d.discount_id}>{d.name} ({discountLabel(d.value, d.is_percentage)})</option>)}
            </select>
            <p className="mt-1 text-xs text-content-3">Si lo pones, al asignar este plan a un alumno el descuento se aplica solo (ej. 2 disciplinas → −$100). Editable por alumno.</p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
              {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear plan'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
